// src/utils/index.js
import { compareAsc, parse } from 'date-fns';
import { TZDate } from "@date-fns/tz";
/**
 * Utility function to combine CSS classes conditionally
 * @param {...any} classes - Class names to combine
 * @returns {string} Combined class names
 */
export const cn = (...classes) => {
  return classes.filter(Boolean).join(" ");
};

/**
 * Debounce function to limit the rate of function execution
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle function to limit function execution frequency
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} Throttled function
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

/**
 * Check if device is mobile
 * @returns {boolean} True if mobile device
 */
export const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

/**
 * Check if PWA is installed
 * @returns {boolean} True if running in standalone mode
 */
export const isPWAInstalled = () => {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone ||
    document.referrer.includes("android-app://")
  );
};

/**
 * Format file size in human readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} Success status
 */
export const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const result = document.execCommand("copy");
      document.body.removeChild(textArea);
      return result;
    }
  } catch (error) {
    console.error("Failed to copy text:", error);
    return false;
  }
};

export const getNzTDateTimeFromCollectionWindow = (window) => {
  const startTime = parse(window.start_time, 'HH:mm:ss', new Date());
  const endTime = parse(window.end_time, 'HH:mm:ss', new Date());
  const start = new TZDate(startTime, "Pacific/Auckland");
  const end = new TZDate(endTime, "Pacific/Auckland");
  return { start, end };
}

export const secondsUntilFutureClose = (store, nowNz) => {
  if (!store?.products?.length) return Infinity;

  let minMs = Infinity;

  for (const p of store.products) {
    if (p.qty_remaining <= 0 || !p.collection_window) continue;

    const { start, end } = getNzTDateTimeFromCollectionWindow(p.collection_window);
    // if start time is in the future, skip
    if (compareAsc(start, nowNz) > 0) continue;
    // if end time is in the past, skip
    if (compareAsc(end, nowNz) < 0) continue;

    const remaining = end.getTime() - nowNz.getTime();

    if (remaining > 0 && remaining < minMs) {
      minMs = remaining;
    }
  }
  return minMs
};


export const getGroupsedStores = (stores) => {
  const nzTimeNow = new TZDate(new Date(), "Pacific/Auckland");
  const currentlyActive = [...stores].filter((store) => {
    const products = Array.isArray(store.products) ? store.products : [];
    return products.some((p) => {
      if (p.qty_remaining <= 0) {
        return false;
      }
      const { start, end } = getNzTDateTimeFromCollectionWindow(p.collection_window);
      // end time is in the past, we dont want this, this is long gone
      if (compareAsc(end, nzTimeNow) < 0) {
        return false;
      }
      // start time is in the future, we filter this out.
      if (compareAsc(start, nzTimeNow) > 0) {
        return false;
      }
      return true
    });
  });

  const urgent = [...currentlyActive].filter(store => {
    const products = Array.isArray(store.products) ? store.products : [];
    return products.some((p) => {

      if (p.qty_remaining <= 0) {
        return false;
      }
      const { start, end } = getNzTDateTimeFromCollectionWindow(p.collection_window);
      // start time is in the past
      if (compareAsc(start, nzTimeNow) <= 0) {
        const timeDiffMs = end.getTime() - nzTimeNow.getTime();
        // if closing within 60 minutes
        if (timeDiffMs > 0 && timeDiffMs <= 3600 * 1000) {
          return true;
        }
      }
      return false;
    });
  });

  const urgentIds = urgent.map(s => s.public_id);

  const now = [...currentlyActive].filter(store => !urgentIds.includes(store.public_id));


  const soon = [...stores].filter((store) => {
    const products = Array.isArray(store.products) ? store.products : [];
    return products.some((p) => {
      if (p.qty_remaining <= 0) {
        return false;
      }
      const { start, end } = getNzTDateTimeFromCollectionWindow(p.collection_window);
      // start time is in the future, we want to show this
      if (compareAsc(start, nzTimeNow) > 0) {
        return true;
      }
      // end time is in the past, we don't want this
      if (compareAsc(end, nzTimeNow) < 0) {
        return false;
      }
      // We will be filtering out currently available via this logic
      return false
    });
  });

  const unavailable = [...stores].filter((store) => {
    const livePublicIds = [...urgent, ...now, ...soon].map(s => s.public_id);
    return !livePublicIds.includes(store.public_id);
  });

  return {
    urgent,
    now,
    soon,
    unavailable
  }
}

export const uniqBy = (arr, keyFn) => {
  const seen = new Set();
  return arr.filter(item => {
    const key = keyFn(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}